import {
  Sensor,
  SensorAlert,
  SensorEvent,
  SensorType,
  useSensorAlerts,
  useSensorEvents,
} from "common";
import { useMemo } from "react";

import { useAppSync } from "../../contexts/AppSyncContext";

export const useLatestSensorEvents = (
  sensorType: SensorType,
): {
  sensor: Sensor | undefined;
  event: SensorEvent | undefined;
  events: SensorEvent[];
  alerts: SensorAlert[];
  inProgress: boolean;
} => {
  const { sensors, sensorsLoading, selectedHub } = useAppSync();
  const selectedHubId = selectedHub?.id;

  const matchedSensor = useMemo(
    () =>
      sensors?.find(
        (sensor) =>
          sensor.hubId === selectedHubId && sensor.type === sensorType,
      ),
    [sensors, selectedHubId, sensorType],
  );

  const { sensorEvents, loading: sensorEventsLoading } = useSensorEvents(
    selectedHubId ?? undefined,
    matchedSensor?.id,
  );

  const { sensorAlerts, loading: sensorAlertsLoading } = useSensorAlerts(
    matchedSensor?.id,
  );

  const filteredSensorEvents = useMemo(
    () =>
      (sensorEvents || []).filter(
        (sensorEvent) => sensorEvent.eventType === "DATA",
      ),
    [sensorEvents],
  );

  const latestEvent = useMemo(
    () =>
      filteredSensorEvents.find(
        (sensorEvent) =>
          sensorEvent.parsedMetadata?.measurementType === sensorType,
      ),
    [filteredSensorEvents, sensorType],
  );

  const inProgress = useMemo(
    () => sensorsLoading || sensorEventsLoading || sensorAlertsLoading,
    [sensorsLoading, sensorEventsLoading, sensorAlertsLoading],
  );

  return useMemo(
    () => ({
      sensor: matchedSensor,
      event: latestEvent,
      events: sensorEvents || [],
      alerts: sensorAlerts || [],
      inProgress,
    }),
    [matchedSensor, latestEvent, sensorEvents, sensorAlerts, inProgress],
  );
};
